import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createRouteConfig } from '../utils/routeGuard';
import { useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';
import { captureUtmParams } from '../utils/utmTracking';

// Import static i18n instance for SSR-safe translations
import { staticI18n } from '../i18n';
import { Header } from '@/components/public/Header';
import { Hero } from '@/components/public/Hero';
import { HowItWorks } from '@/components/public/HowItWorks';
import { BenefitsStrip } from '@/components/public/BenefitsStrip';
import { MobileCTA } from '@/components/public/MobileCTA';
import { PricingTeaser } from '@/components/public/PricingTeaser';
import { FinalCTA } from '@/components/public/FinalCTA';
import { V2Header } from '@/components/public/V2Header';

// Pre-computed SEO translations to ensure they're available during SSR
const seoTitle = staticI18n.t('home.seo.title');
const seoDescription = staticI18n.t('home.seo.description');
const seoOgTitle = staticI18n.t('home.seo.ogTitle');
const seoOgDescription = staticI18n.t('home.seo.ogDescription');

export const Route = createFileRoute('/')({
  component: Index,
  // Add meta tags for SEO
  ...createRouteConfig({
    isPublic: true,
    seo: {
      title: seoTitle,
      description: seoDescription,
      ogTitle: seoOgTitle,
      ogDescription: seoOgDescription,
    },
  }),
});

// Export the component so it can be tested directly
function Index() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();

  // Capture UTM parameters from URL
  useEffect(() => {
    captureUtmParams();
  }, []);

  // Check for auth errors from session storage
  useEffect(() => {
    const authError = window.sessionStorage.getItem('auth_error');
    if (authError) {
      toast.error(authError, {
        duration: 5000,
        position: 'top-center',
        id: 'auth-error',
      });
      // Clear the error after displaying it
      window.sessionStorage.removeItem('auth_error');
    }
  }, []);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate({ to: '/dashboard' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  return (
    <div className='min-h-screen bog-white text-slate-900'>
      <V2Header />
      <Header />
      <Hero />
      <HowItWorks />
      <BenefitsStrip />
      <MobileCTA />
      <PricingTeaser />
      <FinalCTA />
      <Footer />
    </div>
  );
}
